import React from 'react';
import type { SiteRagReport } from '../../../types/siteRag';

type ProviderRelevanceFinding = NonNullable<
  NonNullable<SiteRagReport['qaFindings']>['providerRelevance']
>[number];

function severityClass(severity: ProviderRelevanceFinding['severity']): string {
  switch (severity) {
    case 'block':
      return 'border-danger/40 bg-danger/10 text-text-primary';
    case 'warn':
      return 'border-warning/40 bg-warning/10 text-text-primary';
    default:
      return 'border-border bg-surface-1 text-text-secondary';
  }
}

export function ProviderRelevancePanel({ reports }: { reports: SiteRagReport[] }) {
  const rows = reports.flatMap((report) =>
    (report.qaFindings?.providerRelevance ?? []).map((f) => ({ report, f })),
  );

  if (rows.length === 0) return null;

  return (
    <section className="provider-relevance-panel mt-4" data-testid="provider-relevance-panel">
      <h3 className="text-sm font-bold uppercase tracking-widest text-text-secondary mb-2">
        Provider Relevance (R-77)
      </h3>
      <p className="text-sm text-text-secondary mb-3">
        Per-section provider whitelist checks from the latest report snapshot.
        Off-topic citations warn; sole numeric support from a disallowed provider blocks.
      </p>
      <ul className="space-y-2 text-sm">
        {rows.slice(0, 30).map(({ report, f }) => (
          <li
            key={`${report.id}-${f.sectionId}-${f.kind}-${f.refIndex ?? f.providerId}`}
            className={`rounded border px-3 py-2 ${severityClass(f.severity)}`}
          >
            <span className="font-medium">{report.reportType ?? report.id}</span>
            {' · '}
            <span className="font-mono text-xs">{f.sectionId}</span>
            {' — '}
            {f.refIndex != null ? `[Ref ${f.refIndex}] ` : ''}
            {f.message}
          </li>
        ))}
        {rows.length > 30 && (
          <li className="text-text-secondary">…and {rows.length - 30} more finding(s)</li>
        )}
      </ul>
    </section>
  );
}
